import { useState, useRef } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { ReactComponent as FilterIcon } from './assets/filter.svg';
import { ReactComponent as CloseIcon } from './assets/close.svg';
import { ReactComponent as ChevronIcon } from './assets/chevron.svg';
import { useClickOutside } from '../../utils/useClickOutside';
import { FilterMenuItem } from './components/FilterMenuItem';

import styles from './Filter.module.scss';

export interface FilterProps {
  onClearAll: () => void;
  onApply?: () => void;
  onCancel?: () => void;
  hasChanges?: boolean;
  content: {
    name: string;
    count: number;
    content: React.ReactNode;
    isExclude?: boolean;
    disabled?: boolean;
    tooltipText?: string;
  }[];
}

export const Filter: React.FC<FilterProps> = ({
  onClearAll,
  content
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeFilterIndex, setActiveFilterIndex] = useState(0);
  const clickRef = useRef(null);

  useClickOutside(clickRef, () => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  return (
    <div className={styles.filterWrapper} ref={clickRef}>
      <button
        className={cn(styles.filterButton, { [styles.active]: isOpen })}
        onClick={() => setIsOpen(!isOpen)}
      >
        <FilterIcon />
        <span>{t('All filters')}</span>
        <span className={styles.chevron}>
          <ChevronIcon />
        </span>
      </button>
      {isOpen ? <div className={cn(styles.filterContainer, isOpen ? styles.show : '')}>
        <div className={styles.filterHeader}>
          <h4>{t('Filters')}</h4>
          <div className={styles.filterActions}>
            <button className={styles.clearButton} onClick={onClearAll}>{t('Clear all')}</button>
            <button className={styles.closeButton} onClick={() => setIsOpen(!isOpen)}>
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className={styles.filtersContentWrapper}>
          <div className={styles.filtersList}>
            {content.map((filter, index) => (
              <FilterMenuItem
                filter={filter}
                isActive={index === activeFilterIndex}
                isDisabled={filter.disabled}
                tooltipText={filter.tooltipText}
                onClick={() => !filter.disabled && setActiveFilterIndex(index)}
                key={filter.name}
              />
            ))}
          </div>
          <div className={styles.filterContent}>
            {!content[activeFilterIndex].disabled && content[activeFilterIndex].content}
          </div>
        </div>
      </div> : null}
    </div>
  );
};
